import cx from 'classnames'
import {VentrataDataProvider} from 'components/common/CartItemBuilder/providers/VentrataDataProvider'
import {Warning} from 'components/system/Warning'
import {RadioInput} from 'components/system/inputs/RadioInput'
import {EnvironmentProvider} from 'providers/EnvironmentProvider'
import {useCallback, useMemo} from 'react'
import {Ventrata} from 'types'
import {ga4} from 'utils/ga4'
import {StepDataProvider} from '../../../../providers/StepDataProvider/StepDataProvider'
import {CostFrom} from './CostFrom'

type Props = {
  option: Ventrata.Option
}

export const Option = ({option}: Props) => {
  const totalUnitsCounter = StepDataProvider.guests.useTotalUnitsCounter()
  const dataManager = StepDataProvider.useOptionDataManager()
  const product = VentrataDataProvider.useProduct()
  const currency = EnvironmentProvider.useCurrency()

  const isAvailable = useMemo(() => {
    return !option.restrictions?.maxUnits || totalUnitsCounter <= option.restrictions.maxUnits
  }, [option, totalUnitsCounter])

  const onOptionSelect = useCallback(
    (optionId: string) => {
      dataManager.save(optionId)

      ga4({
        event: 'select_option',
        item_id: product.id,
        item_name: product.title,
        item_variant: option.title,
        checkout_type: 'custom_checkout',
      })
    },
    [product, option, dataManager]
  )

  return (
    <div
      className={cx(
        'p-4 rounded-lg border border-solid',
        option.id === dataManager.getData() ? 'border-theme' : 'border-[#F6F6F6] bg-[#F6F6F6] hover:opacity-80 ',
        isAvailable ? 'cursor-pointer' : 'pointer-events-none text-gray-500'
      )}
      onClick={() => onOptionSelect(option.id)}
    >
      <div className="flex flex-col w-full">
        <div className="flex flex-col gap-1 pb-4">
          <div className="flex justify-between">
            <div className="font-bold text-xl">{option.title}</div>
            <RadioInput checked={option.id === dataManager.getData()} className="!static" />
          </div>
          {option.shortDescription && (
            <div
              className="text-base text-text-secondary"
              dangerouslySetInnerHTML={{__html: option.shortDescription}}
            />
          )}
        </div>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex justify-between">
            <div>Cost:</div>
            <CostFrom product={product} option={option} currency={currency} />
          </div>

          <div className="flex justify-between items-center">
            <div>Duration:</div>
            <div>{option.duration}</div>
          </div>
        </div>
        {!isAvailable && (
          <Warning>
            <strong>Sorry, </strong> this option is not available for more than {option.restrictions?.maxUnits} guests.
          </Warning>
        )}
      </div>
    </div>
  )
}
